import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutlineOutlined,
  CancelOutlined,
  FactCheckOutlined,
  OpenInNewOutlined,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  reportsApi,
  type MeasurementAssuranceResponse,
} from '../../services/reportsApi';
import {
  customerScanCameraLabel,
  customerScanConditionLabel,
  customerScanZoneLabel,
} from '../../domain/acceptance/safetyScanProductPresentation';

type Props = {
  reportId: string;
  /** Draft Safety Scan workspace: emphasize complete-before-sign-off. */
  variant?: 'default' | 'presignoff';
  onAdmissionChange?: (state: {
    admitted: boolean;
    status: string;
    metReady: boolean;
    allowsSignoff: boolean;
    fieldVerificationAllowed: boolean | null;
    fieldVerificationCode: string | null;
    fieldVerificationLabel: string | null;
  }) => void;
};

function formatSupplements(value: Record<string, unknown> | null | undefined): string {
  const payload = value && typeof value === 'object' ? value : {};
  return `${JSON.stringify(payload, null, 2)}\n`;
}

export function MeasurementAssurancePanel({
  reportId,
  variant = 'default',
  onAdmissionChange,
}: Props) {
  const trimmedId = String(reportId || '').trim();
  const preSignOff = variant === 'presignoff';
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [data, setData] = useState<MeasurementAssuranceResponse | null>(null);
  const [evaluationRunId, setEvaluationRunId] = useState('');
  const [supplementsText, setSupplementsText] = useState('{\n}\n');

  const emitAdmission = useCallback((response: MeasurementAssuranceResponse | null) => {
    if (!onAdmissionChange) return;
    const readiness = response?.readiness || null;
    const checks = readiness?.checks || [];
    const freezeAdmitted = checks.some((item) => item.key === 'frozen_run_admitted' && item.ok)
      || (response?.current_measurement as { frozen_run_admitted?: boolean } | null | undefined)?.frozen_run_admitted === true;
    const allowsSignoff = response?.allows_signoff === true
      || (response?.allows_signoff == null && freezeAdmitted);
    const fv = response?.field_verification_gate;
    onAdmissionChange({
      admitted: allowsSignoff,
      status: String(readiness?.current_status || 'NOT_MET').toUpperCase(),
      metReady: readiness?.met_ready === true,
      allowsSignoff,
      fieldVerificationAllowed: fv ? fv.allowed === true : null,
      fieldVerificationCode: fv?.code || null,
      fieldVerificationLabel: fv?.customer_label || null,
    });
  }, [onAdmissionChange]);

  const load = useCallback(async (selectedRunId?: string) => {
    if (!trimmedId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await reportsApi.getMeasurementAssurance(
        trimmedId,
        selectedRunId ? { evaluation_run_id: selectedRunId } : undefined,
      );
      if (!response?.ok) {
        setError(String(response?.error || response?.detail || 'measurement_assurance_failed'));
        setData(null);
        // Keep admission unknown on fetch failure so package downloads are not blocked.
        return;
      }
      setData(response);
      emitAdmission(response);
      const readiness = response.readiness || {};
      const nextRunId = String(
        selectedRunId
        || readiness.evaluation_run_id
        || response.evaluation_run?.id
        || '',
      ).trim();
      setEvaluationRunId(nextRunId);
      setSupplementsText(formatSupplements(readiness.suggested_supplements || {}));
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.code;
      const code = err?.response?.data?.error || err?.message || 'measurement_assurance_failed';
      setError(detail ? `${code}: ${detail}` : String(code));
      setData(null);
      // Keep admission unknown on fetch failure so package downloads are not blocked.
    } finally {
      setLoading(false);
    }
  }, [emitAdmission, trimmedId]);

  useEffect(() => {
    void load();
  }, [load]);

  const readiness = data?.readiness || null;
  const checks = readiness?.checks || [];
  const nextActions = readiness?.next_actions || [];
  const status = String(readiness?.current_status || 'NOT_MET').toUpperCase();
  const metReady = readiness?.met_ready === true;
  const admitted = data?.allows_signoff === true
    || (data?.allows_signoff == null && (
      checks.some((item) => item.key === 'frozen_run_admitted' && item.ok)
      || (data?.current_measurement as { frozen_run_admitted?: boolean } | null | undefined)?.frozen_run_admitted === true
    ));
  const admittedAndMet = admitted && status === 'MET';
  const frozenMeasurement = (data?.current_measurement || {}) as Record<string, unknown>;
  const liveSampleCounts = data?.sample_counts || null;
  const admittedSampleCounts = data?.admitted_sample_counts || null;
  const hasAdmittedSampleCounts = Boolean(
    admittedSampleCounts
    && (
      typeof admittedSampleCounts.labeled === 'number'
      || typeof admittedSampleCounts.total === 'number'
    ),
  );

  const runOptions = useMemo(() => {
    const items = [...(data?.recent_evaluation_runs || [])];
    const currentId = String(data?.evaluation_run?.id || '').trim();
    if (currentId && !items.some((item) => String(item.id || '').trim() === currentId)) {
      items.unshift({
        id: currentId,
        labeled_count: data?.evaluation_run?.labeled_count ?? null,
        precision: (data?.evaluation_run?.metrics_json as { precision?: number } | null)?.precision ?? null,
      });
    }
    return items.filter((item) => String(item.id || '').trim());
  }, [data]);

  const onAdmit = async () => {
    if (!trimmedId || !evaluationRunId) {
      setError('Select an evaluation run before admitting.');
      return;
    }
    let supplements: Record<string, unknown> = {};
    try {
      const parsed = JSON.parse(supplementsText || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('supplements must be a JSON object');
      }
      supplements = parsed as Record<string, unknown>;
    } catch (err: any) {
      setError(`Invalid supplements JSON: ${String(err?.message || err)}`);
      return;
    }

    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const response = await reportsApi.admitReportEvaluation(trimmedId, {
        evaluation_run_id: evaluationRunId,
        admit: true,
        supplements,
      });
      if (!response?.ok) {
        setError(String(response?.error || response?.detail || 'admit_failed'));
        return;
      }
      const measurementStatus = String(
        (response.measurement as { status?: string } | null | undefined)?.status || 'NOT_MET',
      ).toUpperCase();
      setNotice(
        measurementStatus === 'MET'
          ? (preSignOff
            ? 'Admitted. Detection-performance gate is MET. You can continue to Safety Scan sign-off.'
            : 'Admitted. Detection-performance gate is MET. Re-download the customer package to refresh the PDF.')
          : (preSignOff
            ? 'Admitted with NOT_MET. Sign-off can proceed with STOP recommendation; fill remaining checklist items if you need MET.'
            : 'Admitted, but gate remains NOT_MET. Fill remaining checklist items (often recall, κ, usable hours) and admit again.'),
      );
      await load(evaluationRunId);
    } catch (err: any) {
      setError(String(err?.response?.data?.error || err?.message || 'admit_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!trimmedId) return null;

  return (
    <Card
      data-testid="measurement-assurance-panel"
      sx={{
        mb: 3,
        borderRadius: 3,
        border: preSignOff ? '2px solid #F59E0B' : '1px solid #E2E8F0',
        boxShadow: 'none',
        bgcolor: preSignOff ? '#FFFBEB' : undefined,
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} useFlexGap flexWrap="wrap">
          <FactCheckOutlined fontSize="small" color="action" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {admittedAndMet
              ? 'Detection check'
              : (preSignOff ? 'Measurement Assurance (required before sign-off)' : 'Measurement Assurance')}
          </Typography>
          {admittedAndMet ? (
            <Chip
              size="small"
              color="success"
              label="Passed"
              data-testid="detection-check-passed"
            />
          ) : (
            <>
              <Chip
                size="small"
                color={status === 'MET' ? 'success' : 'warning'}
                label={`Gate: ${status}`}
              />
              <Chip
                size="small"
                variant="outlined"
                color={metReady ? 'success' : 'default'}
                label={metReady ? 'Ready to MET if admitted' : 'Not ready for MET'}
              />
            </>
          )}
          {admitted && !admittedAndMet ? <Chip size="small" color="info" label="Frozen detection check recorded" /> : null}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {admittedAndMet
            ? 'A frozen detection-performance check is recorded for this Safety Scan. Signing does not prove the Control worked.'
            : (preSignOff
              ? 'Complete detection-performance evaluation here before signing the Safety Scan. Admit a frozen evaluation even if the gate stays NOT_MET (STOP). Do not invent recall or κ.'
              : 'Detection-performance gate for this Safety Scan. Precision/recall publish only when a frozen evaluation is admitted and thresholds pass. This does not invent missing recall or κ.')}
        </Typography>

        {preSignOff && !admitted ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Safety Scan sign-off stays blocked until you admit a frozen evaluation run below
            (or create/label samples via Accuracy workspace first).
          </Alert>
        ) : null}

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading measurement readiness…
            </Typography>
          </Stack>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {notice ? (
          <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        ) : null}

        {data ? (
          <>
            {admittedAndMet ? (
              <Stack spacing={1.25} sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {typeof frozenMeasurement.precision === 'number' ? (
                    <Chip size="small" variant="outlined" label={`Precision ${frozenMeasurement.precision}`} />
                  ) : null}
                  {typeof frozenMeasurement.recall === 'number' ? (
                    <Chip size="small" variant="outlined" label={`Recall ${frozenMeasurement.recall}`} />
                  ) : null}
                  {typeof frozenMeasurement.false_alerts_per_usable_hour === 'number' ? (
                    <Chip size="small" variant="outlined" label={`False alerts / hour ${frozenMeasurement.false_alerts_per_usable_hour}`} />
                  ) : null}
                  {typeof frozenMeasurement.inter_annotator_kappa === 'number' ? (
                    <Chip size="small" variant="outlined" label={`Reviewer agreement ${frozenMeasurement.inter_annotator_kappa}`} />
                  ) : null}
                  {typeof frozenMeasurement.usable_seconds === 'number' ? (
                    <Chip size="small" variant="outlined" label={`Usable coverage ${frozenMeasurement.usable_seconds}s`} />
                  ) : null}
                </Stack>
                <details data-testid="live-accuracy-advanced">
                  <summary>
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                      Advanced: Live Accuracy workspace
                    </Typography>
                  </summary>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 1 }}>
                    This does not affect the frozen detection check already recorded for this Scan.
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                    <Chip size="small" variant="outlined" label={`Live labeled: ${liveSampleCounts?.labeled ?? 0}`} />
                    <Chip size="small" variant="outlined" label={`Live unlabeled: ${liveSampleCounts?.unlabeled ?? 0}`} />
                    {data.scope?.camera_id ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Camera: ${customerScanCameraLabel(data.scope.camera_id) || data.scope.camera_id}`}
                      />
                    ) : null}
                    {data.scope?.zone_id ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Zone: ${customerScanZoneLabel(data.scope.zone_id) || data.scope.zone_id}`}
                      />
                    ) : null}
                    {data.scope?.policy_type ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Policy: ${customerScanConditionLabel(data.scope.policy_type) || data.scope.policy_type}`}
                      />
                    ) : null}
                  </Stack>
                  {(data.scope?.camera_id || data.scope?.zone_id) ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Technical identifiers: {[data.scope.camera_id, data.scope.zone_id, data.scope.policy_type].filter(Boolean).join(' · ')}
                    </Typography>
                  ) : null}
                </details>
              </Stack>
            ) : (
              <>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
              {admitted ? (
                hasAdmittedSampleCounts ? (
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label={
                      typeof admittedSampleCounts?.total === 'number'
                        ? `Admitted set: ${admittedSampleCounts.labeled ?? 0}/${admittedSampleCounts.total}`
                        : `Admitted labeled: ${admittedSampleCounts?.labeled ?? 0}`
                    }
                  />
                ) : (
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label="Admitted set count unavailable"
                  />
                )
              ) : null}
              <Chip
                size="small"
                variant="outlined"
                label={`Live labeled: ${liveSampleCounts?.labeled ?? 0}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Live unlabeled: ${liveSampleCounts?.unlabeled ?? 0}`}
              />
              {data.scope?.camera_id ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Camera: ${customerScanCameraLabel(data.scope.camera_id) || data.scope.camera_id}`}
                />
              ) : null}
              {data.scope?.zone_id ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Zone: ${customerScanZoneLabel(data.scope.zone_id) || data.scope.zone_id}`}
                />
              ) : null}
              {data.scope?.policy_type ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Policy: ${customerScanConditionLabel(data.scope.policy_type) || data.scope.policy_type}`}
                />
              ) : null}
            </Stack>
            {admitted ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Live sample chips show current Accuracy workspace inventory for this scope. Admitted-set counts come
                from the selected evaluation run when available.
              </Typography>
            ) : null}

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Checklist
              </Typography>
              <Stack spacing={0.75}>
                {checks.map((item) => (
                  <Stack
                    key={item.key}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                    data-testid={`measurement-check-${item.key}`}
                  >
                    {item.ok ? (
                      <CheckCircleOutlineOutlined fontSize="small" color="success" />
                    ) : (
                      <CancelOutlined fontSize="small" color="warning" />
                    )}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.key}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.detail}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
                {!checks.length ? (
                  <Typography variant="body2" color="text.secondary">
                    No checklist returned for this report.
                  </Typography>
                ) : null}
              </Stack>
            </Box>

            {nextActions.length ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Next actions
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {nextActions.map((action) => (
                    <li key={action}>
                      <Typography variant="body2">{action}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            ) : null}
              </>
            )}

            {admittedAndMet ? null : (
            <Stack spacing={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel id="measurement-eval-run-label">Evaluation run</InputLabel>
                <Select
                  labelId="measurement-eval-run-label"
                  label="Evaluation run"
                  value={evaluationRunId}
                  onChange={(event) => {
                    const next = String(event.target.value || '').trim();
                    setEvaluationRunId(next);
                    void load(next || undefined);
                  }}
                  data-testid="measurement-evaluation-run-select"
                >
                  {!runOptions.length ? (
                    <MenuItem value="">
                      <em>No evaluation runs for this scope</em>
                    </MenuItem>
                  ) : null}
                  {runOptions.map((run) => {
                    const id = String(run.id || '').trim();
                    const precision =
                      typeof run.precision === 'number' ? ` · precision ${run.precision}` : '';
                    const labeled =
                      typeof run.labeled_count === 'number' ? ` · labeled ${run.labeled_count}` : '';
                    return (
                      <MenuItem key={id} value={id}>
                        {id}
                        {labeled}
                        {precision}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <TextField
                label="Supplements JSON"
                value={supplementsText}
                onChange={(event) => setSupplementsText(event.target.value)}
                multiline
                minRows={6}
                fullWidth
                size="small"
                helperText="Fill recall and inter_annotator_kappa. usable_seconds auto-derives false alerts/hour when FP counts exist."
                inputProps={{ 'data-testid': 'measurement-supplements-json' }}
              />

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                <Button
                  variant="contained"
                  onClick={() => void onAdmit()}
                  disabled={submitting || loading || !evaluationRunId}
                  data-testid="measurement-admit-button"
                >
                  {submitting ? 'Admitting…' : 'Admit evaluation'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => void load(evaluationRunId || undefined)}
                  disabled={loading || submitting}
                >
                  Refresh
                </Button>
                <Button
                  component={RouterLink}
                  to="/accuracy"
                  variant="text"
                  endIcon={<OpenInNewOutlined fontSize="small" />}
                >
                  Open Accuracy workspace
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {preSignOff
                    ? 'Admit here first. Then use Sign Safety Scan below.'
                    : 'After admit, re-download the customer package to refresh Measurement Assurance in the PDF.'}
                </Typography>
              </Stack>
            </Stack>
            )}
          </>
        ) : null}

        {!loading && !data && !error ? (
          <Typography variant="body2" color="text.secondary">
            Measurement readiness is unavailable for this report.
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default MeasurementAssurancePanel;
